/* eslint-disable import/prefer-default-export */
import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight2, Clock, SearchNormal1 } from "iconsax-react";
import styled from "styled-components";

import { CATEGORY_COLORS, MAP_COPY } from "../../constants";
import { useDebounce } from "../../hooks/useDebounce";
import {
  filterMapPois,
  getPoisMostAccessed,
  getPoisRecentAccessed,
  isPoiNearbyToUser,
} from "../../services/mapService";
import type { MapPoiListItem } from "../../services/map.types";
import { CategoryIcon } from "../../utils/categoryIcons";
import { PoiNearbyTag } from "../PoiNearbyTag";

const Overlay = styled.div<{ $top: number; $bottom: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({ $top }) => $top}px;
  bottom: ${({ $bottom }) => $bottom}px;
  z-index: 500;
  background: ${({ theme }) => theme.colors.background};
  overflow: auto;
  padding: 8px 12px 24px;
`;

const SectionTitle = styled.h4`
  margin: 16px 8px 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const ItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: 19px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ItemAddress = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 8px;
`;

const SuggestionChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  cursor: pointer;
`;

type MapSearchOverlayProps = {
  visible: boolean;
  searchText: string;
  topOffset: number;
  bottomOffset?: number;
  userLatitude?: number;
  userLongitude?: number;
  onSelectSearch: (text: string) => void;
  onSelectPoi?: (poiId: string) => void;
};

function PoiSearchItem({
  poi,
  userLatitude,
  userLongitude,
  onPress,
}: {
  poi: MapPoiListItem;
  userLatitude?: number;
  userLongitude?: number;
  onPress: (poi: MapPoiListItem) => void;
}) {
  const categoryColor = CATEGORY_COLORS[poi.category];
  const isNearby = isPoiNearbyToUser(poi, userLatitude, userLongitude);

  return (
    <Item type="button" onClick={() => onPress(poi)}>
      <CategoryIcon category={poi.category} size={22} />
      <ItemText>
        <ItemTitle>{poi.title}</ItemTitle>
        {poi.address ? <ItemAddress>{poi.address}</ItemAddress> : null}
      </ItemText>
      {isNearby ? (
        <PoiNearbyTag accentColor={categoryColor} />
      ) : (
        <ArrowRight2 size={18} color="var(--color-text-secondary)" />
      )}
    </Item>
  );
}

export function MapSearchOverlay({
  visible,
  searchText,
  topOffset,
  bottomOffset = 0,
  userLatitude,
  userLongitude,
  onSelectSearch,
  onSelectPoi,
}: MapSearchOverlayProps) {
  const debouncedSearch = useDebounce(searchText, 400);
  const isTyping = searchText.trim().length > 0;
  const [mostAccessed, setMostAccessed] = useState<MapPoiListItem[]>([]);
  const [recentAccessed, setRecentAccessed] = useState<MapPoiListItem[]>([]);

  useEffect(() => {
    if (!visible) return;
    setMostAccessed(getPoisMostAccessed());
    setRecentAccessed(getPoisRecentAccessed());
  }, [visible]);

  const typingResults = useMemo(
    () => filterMapPois(debouncedSearch),
    [debouncedSearch]
  );

  const searchItems = useMemo(
    () =>
      typingResults.map((poi) => ({
        id: poi.id,
        title: poi.name,
        category: poi.category,
        address: "",
        latitude: poi.latitude,
        longitude: poi.longitude,
        state: poi.state,
      })),
    [typingResults]
  );

  if (!visible) return null;

  const handlePoiPress = (poi: MapPoiListItem) => {
    onSelectPoi?.(poi.id);
  };

  return (
    <Overlay $top={topOffset} $bottom={bottomOffset}>
      {!isTyping ? (
        <>
          <ChipRow>
            {["hospital", "escola", "libras", "associacao"].map((term) => (
              <SuggestionChip
                key={term}
                type="button"
                onClick={() => onSelectSearch(term)}
              >
                <SearchNormal1 size={16} />
                {term}
              </SuggestionChip>
            ))}
          </ChipRow>

          {recentAccessed.length > 0 ? (
            <>
              <SectionTitle>{MAP_COPY.recentAccessed}</SectionTitle>
              {recentAccessed.map((poi) => (
                <PoiSearchItem
                  key={poi.id}
                  poi={poi}
                  userLatitude={userLatitude}
                  userLongitude={userLongitude}
                  onPress={handlePoiPress}
                />
              ))}
            </>
          ) : null}

          {mostAccessed.length > 0 ? (
            <>
              <SectionTitle>{MAP_COPY.mostAccessed}</SectionTitle>
              {mostAccessed.map((poi) => (
                <PoiSearchItem
                  key={poi.id}
                  poi={poi}
                  userLatitude={userLatitude}
                  userLongitude={userLongitude}
                  onPress={handlePoiPress}
                />
              ))}
            </>
          ) : null}
        </>
      ) : (
        <>
          <SectionTitle>{MAP_COPY.searchResults}</SectionTitle>
          {searchItems.length === 0 ? (
            <Item as="div" style={{ cursor: "default" }}>
              <Clock size={20} />
              <ItemText>
                <ItemTitle>{MAP_COPY.noResults}</ItemTitle>
              </ItemText>
            </Item>
          ) : (
            searchItems.map((poi) => (
              <PoiSearchItem
                key={poi.id}
                poi={poi}
                userLatitude={userLatitude}
                userLongitude={userLongitude}
                onPress={handlePoiPress}
              />
            ))
          )}
        </>
      )}
    </Overlay>
  );
}
